import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import AppBar from '@mui/material/AppBar';

import {
  Avatar,
  Box,
  Button,
  Menu,
  MenuItem,
  Radio,
  Toolbar,
  Tooltip,
  Typography,
  darken,
  styled,
  useTheme,
} from '@mui/material';
import ExploreIcon from '@mui/icons-material/Explore';
import Layout from 'components/Layout';
import {
  setUserSelector,
  setDatabaseData,
  setTour,
  setUser,
  setToken,
  customerAccountsTokenSelector,
  activeAccountSelector,
  setActiveAccount,
  fetchWorkforceQuery,
} from 'redux/users/ducks';

import * as CONST from './const';

import LanguageSwitcher from 'components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { USER_LS, USER_ROLES } from 'utils/string_utils';
import { CgArrowLongUpL } from 'react-icons/cg';
import { ReactComponent as Settings } from 'assets/svg/common/settings.svg';
import i18next from 'i18next';
import { HomeSearchBar } from '../Home/HomeSearchBar';
import { TbArrowGuide } from 'react-icons/tb';
import { FleetManagementLogin } from 'services/auth_service';
import { encrypter } from 'utils/Encrypter';
import LoadAnimation from 'components/LoadAnimation';
import { setNotification } from 'redux/notifications/ducks';

const Dashboard = ({ children }) => {
  const { classes } = CONST.useStyles();
  const userLs = JSON.parse(USER_LS);
  const dispatch = useDispatch();
  const user = useSelector(setUserSelector) || userLs;
  const userIsAReporter = user?.iam_role === USER_ROLES.reporter;
  const theme = useTheme();
  const { t } = useTranslation();
  let navigate = useNavigate();
  const fm =
    useSelector(state => state.users.fleetManager) ||
    (localStorage.getItem('fm')
      ? JSON.parse(localStorage.getItem('fm'))
      : null);
  const token = useSelector(customerAccountsTokenSelector);
  const activeAcct =
    useSelector(activeAccountSelector) ||
    JSON.parse(localStorage.getItem('activeAccount'));
  const [state, setState] = useState({
    status: 'idle',
    selectedUser: activeAcct,
    combinedUsers: null,
    anchorEl: null,
    hover: '',
  });
  const open = Boolean(state.anchorEl);

  const updateState = newState => {
    setState(prevState => ({ ...prevState, ...newState }));
  };
  const handleClick = event => {
    updateState({ anchorEl: event.currentTarget });
  };
  const handleClose = () => {
    updateState({ anchorEl: null });
  };

  const handleUserChange = async newSelection => {
    updateState({ status: 'loading' });
    const isMainAccount = newSelection?.customer_id === user?.customer_id;
    let res;
    try {
      if (!isMainAccount) {
        res = await FleetManagementLogin(newSelection.customer_id);
        if (res.status === 200) {
          updateState({ anchorEl: null, selectedUser: newSelection });
          dispatch(setUser(res.data.user, 'login'));
          dispatch(setToken(res.data.access_token));
          dispatch(fetchWorkforceQuery());
          localStorage.setItem(
            'access_token',
            encrypter(res.data.access_token)
          );
          localStorage.setItem(
            'refresh_token',
            encrypter(res.data.refresh_token)
          );
          localStorage.setItem('user', JSON.stringify(res?.data?.user));
          dispatch(setActiveAccount(newSelection));
          localStorage.setItem('activeAccount', JSON.stringify(newSelection));
        } else {
          dispatch(
            setNotification(
              'An error occurred. Please try again',
              'error',
              true
            )
          );
        }
      } else {
        dispatch(setToken(token));
        dispatch(
          setActiveAccount(JSON.parse(localStorage.getItem('mainUser')))
        );
        dispatch(fetchWorkforceQuery());
        dispatch(
          setUser(JSON.parse(localStorage.getItem('mainUser')), 'login')
        );
        localStorage.setItem('activeAccount', localStorage.getItem('mainUser'));
        updateState({ anchorEl: null, selectedUser: newSelection });
      }

      navigate('/home');
    } catch (error) {
      console.error('E21002', error);
    } finally {
      updateState({ status: 'idle' });
    }
  };

  useEffect(() => {
    if (!user) {
      return;
    }

    if (user.is_onboarded) {
      dispatch(setDatabaseData(user.customer_id));
    } else {
      dispatch(setDatabaseData('myFirstDatabase'));
    }
    let combined_users = [];
    combined_users.push(user);
    if (fm?.length > 0) {
      combined_users = combined_users.concat(fm);
    }
    updateState({ combinedUsers: combined_users });

    // eslint-disable-next-line
  }, [user]);

  const renderSubsciptionButton = useCallback(() => {
    if (userIsAReporter) {
      return (
        <Typography variant='h6' className={classes.header1}>
          Read-only
        </Typography>
      );
    }

    return (
      <Button
        variant='contained'
        color='primary'
        id='eighth-step'
        onClick={() => navigate('/plans')}
        startIcon={<CgArrowLongUpL />}
        className={classes.upgradeBtn}
      >
        {t('subscriptionBtn')}
      </Button>
    );
  }, [userIsAReporter, classes, navigate, t]);

  function stringToColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  }

  const BpIcon = styled('span')(({ theme }) => ({
    borderRadius: '50%',
    width: 16,
    height: 16,
    boxShadow:
      'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    backgroundImage:
      'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
    '.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    'input:hover ~ &': {
      backgroundColor: '#ebf1f5',
      ...theme.applyStyles('dark', {
        backgroundColor: '#30404d',
      }),
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: 'rgba(206,217,224,.5)',
      ...theme.applyStyles('dark', {
        background: 'rgba(57,75,89,.5)',
      }),
    },
    ...theme.applyStyles('dark', {
      boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
      backgroundColor: '#394b59',
      backgroundImage:
        'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
    }),
  }));

  const BpCheckedIcon = styled(BpIcon)({
    backgroundColor: theme.palette.primary.main,
    backgroundImage:
      'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&::before': {
      display: 'block',
      width: 16,
      height: 16,
      backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
      content: '""',
    },
    'input:hover ~ &': {
      backgroundColor: darken(theme.palette.primary.main, 0.2),
    },
  });

  const controlProps = item => {
    return {
      checked: state.selectedUser?.customer_id === item.customer_id,
      value: item.customer_id,
      name: 'color-radio-button-demo',
      inputProps: { 'aria-label': item },
      checkedIcon: <BpCheckedIcon />,
      size: 'small',
    };
  };

  function stringAvatar(name = '') {
    const nameParts = name.trim().split(' '); // Split name into parts
    const firstInitial = nameParts[0]?.[0] || ''; // First initial, if available
    const secondInitial = nameParts[1]?.[0] || ''; // Second initial, if available

    return {
      sx: {
        bgcolor: stringToColor(name), // Generate color from full name
      },
      children: `${firstInitial.toUpperCase()}${secondInitial}`, // Combine initials
    };
  }

  return (
    <Layout>
      <LoadAnimation
        loading={state.status === 'loading'}
        message={t('common.switch')}
      />
      <div className={classes.root}>
        <AppBar className={classes.appBar}>
          <Toolbar>
            <Box className={classes.headerContainer}>
              <Box />

              <Box alignSelf='flex-end' className={classes.subscriptionButton}>
                <Button
                  variant='outlined'
                  color='primary'
                  id='guide'
                  onClick={() => navigate('/guides')}
                  startIcon={<TbArrowGuide />}
                  className={classes.upgradeBtn}
                >
                  {t(`guidesPage.heading`)}
                </Button>
                <HomeSearchBar />
                {renderSubsciptionButton()}
                <Box display={'flex'} alignItems='center'>
                  <Tooltip title={t('common.pageTour')}>
                    <ExploreIcon
                      color='primary'
                      className={classes.tourIcon}
                      onClick={() => dispatch(setTour(true))}
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <LanguageSwitcher flag={i18next.language} />
                </Box>
                {fm && fm?.length > 0 && (
                  <Box
                    onClick={handleClick}
                    sx={{ cursor: 'pointer' }}
                    data-hook='avatar'
                  >
                    <Avatar
                      {...stringAvatar(
                        state.selectedUser?.name ||
                          `${state.selectedUser?.first_name || ''} ${
                            state.selectedUser?.last_name || ''
                          }`.trim()
                      )}
                    />
                  </Box>
                )}
                <Menu
                  id='basic-menu'
                  anchorEl={state.anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button',
                  }}
                  data-hook='menu'
                  className={classes.menu}
                >
                  <span
                    textAlign={'center'}
                    variant='caption'
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      padding: '16px',
                    }}
                  >
                    {t('common.accounts')}
                  </span>
                  {state.combinedUsers?.map((user_, i) => (
                    <MenuItem
                      data-hook={`customer_${i}`}
                      onClick={() => {
                        if (
                          state.selectedUser?.customer_id !== user_.customer_id
                        ) {
                          handleUserChange(user_);
                        }
                      }}
                    >
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        width='100%'
                        className={classes.avatarBox}
                        sx={{
                          border: `2px solid ${
                            state.selectedUser?.customer_id ===
                            user_.customer_id
                              ? theme.palette.primary.main
                              : '#E6E6E6'
                          }`,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            transform: 'scale(0.98)',
                          },
                          opacity:
                            state.selectedUser?.customer_id ===
                            user_.customer_id
                              ? 1
                              : 0.8,
                          transition: 'all 0.3s ease',
                        }}
                        // border={'2px solid #B3B3B2'}
                        onMouseEnter={() => updateState({ hover: true })}
                        onMouseLeave={() => updateState({ hover: false })}
                      >
                        <Box display={'flex'} alignItems={'center'} gap={'5px'}>
                          <Avatar
                            {...stringAvatar(
                              user_?.name ||
                                `${user_?.first_name || ''} ${
                                  user_?.last_name || ''
                                }`.trim()
                            )}
                          />
                          <span>
                            {user_?.name ||
                              `${user_.first_name} ${user_.last_name}`}
                          </span>
                        </Box>
                        <Radio {...controlProps(user_)} />
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
                <NavLink to='/settings'>
                  <Settings width={35} />
                </NavLink>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        <main className={classes.content}>
          <div className={classes.toolbar} />
          {children}
        </main>
      </div>
    </Layout>
  );
};

export default React.memo(Dashboard);
